import { parseRemixDataConsumerSource } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { getMauiIdFromAdfNode } from '@atlassian/malleable-ui-components/editor-remix/remix-ids';

import type { CwrRemixResult, CwrRemixVisualMetadata } from '../types/cwr-remix-result';

type AdfNode = {
	attrs?: Record<string, unknown>;
	content?: unknown;
	marks?: Array<{ attrs?: { sources?: unknown }; type?: string }>;
	type?: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const getRemixSource = (node: AdfNode): string | undefined => {
	const sources = node.marks?.find((mark) => mark.type === 'dataConsumer')?.attrs?.sources;
	return Array.isArray(sources) && typeof sources[0] === 'string' ? sources[0] : undefined;
};

const getUserAction = (operation: string): CwrRemixResult['userAction'] =>
	operation === 'replaceNode' ? 'edit' : 'insert';

const collectCwrVisualCandidates = (
	node: unknown,
	operation: string,
	results: CwrRemixResult[],
	visualMetadataByResultId?: ReadonlyMap<string, CwrRemixVisualMetadata>,
): void => {
	if (Array.isArray(node)) {
		node.forEach((child) =>
			collectCwrVisualCandidates(child, operation, results, visualMetadataByResultId),
		);
		return;
	}
	if (!isRecord(node)) {
		return;
	}
	const adfNode = node as AdfNode;

	const mauiId = getMauiIdFromAdfNode(adfNode);
	const localId = adfNode.attrs?.localId;
	const mauiVisualMetadata = mauiId ? visualMetadataByResultId?.get(mauiId) : undefined;
	if (mauiId && typeof localId === 'string') {
		results.push({
			createdContentMetadata: { remixId: mauiId },
			// Page creation's final ADF supplies a native embed but no semantic app type.
			createdContentType: mauiVisualMetadata?.dynamicUiType ?? 'maui-app',
			...(mauiVisualMetadata?.dynamicUiSubtype && {
				dynamicUiSubtype: mauiVisualMetadata.dynamicUiSubtype,
			}),
			identity: { attribute: 'mauiVersion', localId, value: mauiId },
			userAction: getUserAction(operation),
		});
	}

	if (adfNode.type === 'media') {
		const mediaFileId = adfNode.attrs?.id;
		const remixSource = getRemixSource(adfNode);
		const remixMetadata = parseRemixDataConsumerSource(remixSource);
		if (typeof mediaFileId === 'string') {
			const mediaVisualMetadata = visualMetadataByResultId?.get(mediaFileId);
			results.push({
				createdContentMetadata: { mediaFileId },
				createdContentType: mediaVisualMetadata?.dynamicUiType ?? remixMetadata?.type ?? 'image',
				...(mediaVisualMetadata?.dynamicUiSubtype
					? { dynamicUiSubtype: mediaVisualMetadata.dynamicUiSubtype }
					: remixMetadata && { dynamicUiSubtype: remixMetadata.subtype }),
				identity: { attribute: 'id', value: mediaFileId },
				userAction: getUserAction(operation),
			});
		}
	}

	collectCwrVisualCandidates(adfNode.content, operation, results, visualMetadataByResultId);
};

/**
 * Extracts visual candidates from completed CWR ADF. Update callers compare identities with the
 * pre-operation document; whole-page Create callers deliberately register final-document visuals.
 */
export const extractCwrRemixResults = (
	operation: string,
	value: string,
	visualMetadataByResultId?: ReadonlyMap<string, CwrRemixVisualMetadata>,
): CwrRemixResult[] => {
	try {
		const results: CwrRemixResult[] = [];
		collectCwrVisualCandidates(JSON.parse(value), operation, results, visualMetadataByResultId);
		return results;
	} catch {
		return [];
	}
};
